import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Heart, ChatCircle, ShieldCheck, Flag, Prohibit, Camera, ArrowLeft, Crown, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import api, { API } from '../lib/api';
import { SILHOUETTE_AVATAR } from '../lib/avatar';

const COUNTRIES_MAP = {
  slovenia: 'Slovenia', croatia: 'Croatia', bosnia: 'Bosnia & Herzegovina',
  serbia: 'Serbia', montenegro: 'Montenegro', north_macedonia: 'North Macedonia', kosovo: 'Kosovo'
};

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, updateUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportReason, setReportReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [paymentToast, setPaymentToast] = useState('');
  const [adminIntel, setAdminIntel] = useState(null);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminConfirm, setAdminConfirm] = useState(null); // { action, label, run }

  const isOwnProfile = userId === 'me' || userId === currentUser?.id;

  // Fetch admin intel (lifecycle signals) when an admin views any profile
  const refreshAdminIntel = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/admin/users/${pid}/intel`);
      setAdminIntel(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (!currentUser?.is_admin || !profile?.id) return;
    refreshAdminIntel(profile.id);
  }, [currentUser?.is_admin, profile?.id, refreshAdminIntel]);

  const runAdminAction = async (kind) => {
    if (!profile?.id) return;
    setAdminBusy(true);
    try {
      const pid = profile.id;
      if (kind === 'ban')            await api.put(`/admin/users/${pid}/ban`);
      else if (kind === 'unban')     await api.put(`/admin/users/${pid}/unban`);
      else if (kind === 'verify')    await api.put(`/admin/users/${pid}/verify`);
      else if (kind === 'unverify')  await api.put(`/admin/users/${pid}/unverify`);
      else if (kind === 'premium')   await api.put(`/admin/users/${pid}/grant-premium`);
      else if (kind === 'unpremium') await api.put(`/admin/users/${pid}/revoke-premium`);
      else if (kind === 'welcome')   await api.post(`/admin/users/${pid}/send-welcome-email`);
      else if (kind === 'impersonate') {
        const { data } = await api.post(`/admin/users/${pid}/impersonate`);
        // Back up the admin's token so we can restore on exit
        const current = localStorage.getItem('yublink_token');
        if (current) localStorage.setItem('yublink_admin_token', current);
        localStorage.setItem('yublink_token', data.token);
        localStorage.setItem('yublink_impersonating', JSON.stringify({
          target_id: data.target.id,
          email: data.target.email,
          first_name: data.target.first_name,
          started_at: Date.now(),
        }));
        // Hard reload so AuthProvider re-fetches /auth/me with the new token
        window.location.href = '/browse';
        return;
      }
      await refreshAdminIntel(pid);
      setAdminConfirm(null);
    } catch (e) {
      alert(`Action failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setAdminBusy(false);
    }
  };

  const askConfirm = (kind, label) => setAdminConfirm({ kind, label });

  // Always refresh the logged-in user from the backend when landing on their own profile.
  // Ensures is_premium/is_verified reflect the latest DB state (e.g. after a webhook fires).
  useEffect(() => {
    if (isOwnProfile) checkAuth();
  }, [isOwnProfile, checkAuth]);

  // Post-Stripe-checkout redirect lands here with ?session_id=cs_...
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !currentUser) return;
    let cancelled = false;
    const verify = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          updateUser({ is_premium: true, is_verified: true });
          // Re-pull authoritative user state from backend to catch any webhook-set fields
          checkAuth();
          setPaymentToast('🎉 Welcome to Premium! Unlimited chats unlocked.');
          setTimeout(() => setPaymentToast(''), 5000);
        }
      } catch (err) {
        console.error('[profile.payment-verify] failed', err?.response?.data || err);
      } finally {
        // Clean session_id from URL
        const next = new URLSearchParams(searchParams);
        next.delete('session_id');
        setSearchParams(next, { replace: true });
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [searchParams, currentUser, updateUser, setSearchParams, checkAuth]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      if (isOwnProfile && currentUser) {
        setProfile(currentUser);
      } else {
        const { data } = await api.get(`/users/${userId}`);
        setProfile(data.user);
      }
    } catch {
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser, isOwnProfile, navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const updatedPhotos = [...(profile.photos || []), data.photo];
      setProfile(prev => ({ ...prev, photos: updatedPhotos }));
      updateUser({ photos: updatedPhotos });
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await api.post(`/users/${profile.id}/report`, { reason: reportReason });
      setReportReason('');
    } catch {}
  };

  const handleBlock = async () => {
    try {
      await api.post(`/users/${profile.id}/block`);
      navigate('/browse');
    } catch {}
  };

  const getPhotoUrl = (photo) => {
    if (photo?.url) return photo.url;
    if (photo?.storage_path) return `${API}/files/${photo.storage_path}`;
    return SILHOUETTE_AVATAR;
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const photos = profile.photos?.length > 0 ? profile.photos : [{ url: SILHOUETTE_AVATAR }];
  const age = profile.dob ? Math.floor((Date.now() - new Date(profile.dob).getTime()) / 31557600000) : null;

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="profile-page">
      {paymentToast && (
        <div
          data-testid="payment-success-toast"
          className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-2xl bg-[#D4AF37] text-[#060A1A] font-bold shadow-2xl animate-in fade-in slide-in-from-top-2"
        >
          {paymentToast}
        </div>
      )}
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#9EABCE] hover:text-white mb-4 transition-colors" data-testid="profile-back-btn">
          <ArrowLeft size={20} /> Back
        </button>

        {/* Admin-only intel banner */}
        {currentUser?.is_admin && adminIntel && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-intel-banner"
            className="mb-4 rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0B112B] via-[#0B112B] to-[#1E2A52]/40 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.35)]"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} weight="fill" className="text-[#D4AF37]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Admin View</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {adminIntel.user?.is_banned && <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 font-bold">Banned</span>}
                {adminIntel.user?.is_premium && <span className="text-[10px] px-2 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] font-bold">Premium</span>}
                {adminIntel.user?.is_verified && <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/15 text-green-400 font-bold">Verified</span>}
                {!adminIntel.user?.profile_complete && <span className="text-[10px] px-2 py-0.5 rounded bg-orange-500/15 text-orange-400 font-bold">Incomplete</span>}
              </div>
            </div>

            <p className="text-xs text-[#9EABCE] mb-3 truncate">{adminIntel.user?.email}</p>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
              {[
                { label: 'Joined', value: adminIntel.joined_at ? new Date(adminIntel.joined_at).toLocaleDateString() : '—' },
                { label: 'Sessions', value: adminIntel.total_sessions ?? 0 },
                { label: 'Blinks Sent', value: adminIntel.blinks_sent ?? 0 },
                { label: 'Blinks Recv', value: adminIntel.blinks_received ?? 0 },
                { label: 'Messages', value: adminIntel.messages_sent ?? 0 },
                { label: 'Revenue', value: `€${(adminIntel.lifetime_revenue_eur ?? 0).toFixed(2)}` },
              ].map(s => (
                <div key={s.label} className="rounded-lg bg-white/5 px-2 py-2 border border-white/5">
                  <p className="text-[9px] uppercase tracking-wider text-[#9EABCE]/70">{s.label}</p>
                  <p className="text-sm font-bold text-white mt-0.5 truncate">{s.value}</p>
                </div>
              ))}
            </div>

            {(adminIntel.reports_against > 0 || adminIntel.reports_filed_by > 0) && (
              <div className="mt-3 flex items-center gap-3 text-[11px] text-[#9EABCE]">
                <span className={`flex items-center gap-1 ${adminIntel.reports_against > 0 ? 'text-red-400 font-semibold' : ''}`}>
                  <Flag size={11} weight="fill" /> {adminIntel.reports_against} reports against
                </span>
                <span className="opacity-60">•</span>
                <span>{adminIntel.reports_filed_by} reports filed</span>
              </div>
            )}

            {/* Admin Action Buttons */}
            {!isOwnProfile && (
              <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap gap-2" data-testid="admin-actions-row">
                {adminIntel.user?.is_banned ? (
                  <button
                    onClick={() => askConfirm('unban', 'Unban this user?')}
                    disabled={adminBusy}
                    data-testid="admin-action-unban"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-all disabled:opacity-50"
                  >
                    Unban
                  </button>
                ) : (
                  <button
                    onClick={() => askConfirm('ban', 'Ban this user? They will be hidden and blocked.')}
                    disabled={adminBusy}
                    data-testid="admin-action-ban"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-all disabled:opacity-50"
                  >
                    Ban
                  </button>
                )}
                {adminIntel.user?.is_verified ? (
                  <button
                    onClick={() => askConfirm('unverify', 'Remove Verified badge?')}
                    disabled={adminBusy}
                    data-testid="admin-action-unverify"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white/5 text-[#9EABCE] hover:bg-white/10 transition-all disabled:opacity-50"
                  >
                    Unverify
                  </button>
                ) : (
                  <button
                    onClick={() => askConfirm('verify', 'Grant Verified badge?')}
                    disabled={adminBusy}
                    data-testid="admin-action-verify"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-all disabled:opacity-50"
                  >
                    Verify
                  </button>
                )}
                {adminIntel.user?.is_premium ? (
                  <button
                    onClick={() => askConfirm('unpremium', 'Revoke Premium access?')}
                    disabled={adminBusy}
                    data-testid="admin-action-unpremium"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white/5 text-[#9EABCE] hover:bg-white/10 transition-all disabled:opacity-50"
                  >
                    Revoke Premium
                  </button>
                ) : (
                  <button
                    onClick={() => askConfirm('premium', 'Grant Premium access to this user?')}
                    disabled={adminBusy}
                    data-testid="admin-action-premium"
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition-all disabled:opacity-50"
                  >
                    Grant Premium
                  </button>
                )}
                <button
                  onClick={() => askConfirm('welcome', 'Send the welcome email to this user?')}
                  disabled={adminBusy}
                  data-testid="admin-action-welcome"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition-all disabled:opacity-50"
                >
                  Send Welcome Email
                </button>
                <button
                  onClick={() => askConfirm('impersonate', `Step into ${adminIntel.user?.first_name || 'this user'}'s session? Every action you take will be attributed to them. Exit from the gold banner at the top.`)}
                  disabled={adminBusy}
                  data-testid="admin-action-impersonate"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 transition-all disabled:opacity-50"
                >
                  Impersonate
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Admin confirmation modal */}
        <Dialog open={!!adminConfirm} onOpenChange={(v) => !v && setAdminConfirm(null)}>
          <DialogContent className="bg-[#0B112B] border-white/10" data-testid="admin-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-['Outfit']">Confirm Action</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-[#9EABCE] text-sm">{adminConfirm?.label}</p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setAdminConfirm(null)}
                  disabled={adminBusy}
                  data-testid="admin-confirm-cancel"
                  className="px-4 py-2 rounded-xl text-sm bg-white/5 text-[#9EABCE] hover:bg-white/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => adminConfirm && runAdminAction(adminConfirm.kind)}
                  disabled={adminBusy}
                  data-testid="admin-confirm-ok"
                  className="px-4 py-2 rounded-xl text-sm font-bold bg-[#D4AF37] text-[#060A1A] hover:brightness-110 transition-all disabled:opacity-50"
                >
                  {adminBusy ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card overflow-hidden">
          {/* Photo Gallery */}
          <div className="relative aspect-[4/5] md:aspect-[3/4] max-h-[500px]">
            <img
              src={getPhotoUrl(photos[selectedPhoto])}
              alt={profile.first_name}
              className="w-full h-full object-cover"
              data-testid="profile-main-photo"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

            {photos.length > 1 && (
              <div className="absolute top-4 left-4 right-4 flex gap-1">
                {photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedPhoto(i)}
                    className={`h-1 flex-1 rounded-full transition-all ${i === selectedPhoto ? 'bg-[#D4AF37]' : 'bg-white/30'}`}
                  />
                ))}
              </div>
            )}

            {isOwnProfile && (
              <div className="flex gap-2 absolute top-4 right-4">
                <button
                  onClick={() => navigate('/profile/edit')}
                  className="p-3 rounded-xl bg-black/40 backdrop-blur-sm text-[#D4AF37] hover:bg-black/60 transition-all"
                  data-testid="edit-profile-btn"
                >
                  <PencilSimple size={20} weight="light" />
                </button>
                <label className="p-3 rounded-xl bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 cursor-pointer transition-all" data-testid="upload-photo-btn">
                  <Camera size={20} weight="light" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleUploadPhoto} disabled={uploading} />
                </label>
              </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-semibold text-white font-['Outfit']">
                  {profile.first_name}{age ? `, ${age}` : ''}
                </h1>
                {profile.is_verified && <span className="verified-badge">Verified</span>}
                {profile.is_premium && <Crown size={20} weight="fill" className="text-[#D4AF37]" />}
              </div>
              <div className="flex items-center gap-2 text-white/70 text-sm mt-2">
                <MapPin size={16} weight="light" />
                <span>{profile.current_city || COUNTRIES_MAP[profile.heritage_country] || 'Unknown'}</span>
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="p-6 space-y-6">
            {profile.bio && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-2">About</p>
                <p className="text-[#9EABCE] text-sm leading-relaxed">{profile.bio}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {profile.heritage_country && (
                <div className="glass-card p-3">
                  <p className="text-xs text-[#9EABCE]/60 mb-1">Heritage</p>
                  <p className="text-white text-sm font-medium">{COUNTRIES_MAP[profile.heritage_country]}</p>
                </div>
              )}
              {profile.current_city && (
                <div className="glass-card p-3">
                  <p className="text-xs text-[#9EABCE]/60 mb-1">Lives in</p>
                  <p className="text-white text-sm font-medium">{profile.current_city}</p>
                </div>
              )}
            </div>

            {/* Photo Thumbnails */}
            {photos.length > 1 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Photos</p>
                <div className="grid grid-cols-5 gap-2">
                  {photos.map((photo, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedPhoto(i)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === selectedPhoto ? 'border-[#D4AF37]' : 'border-transparent'}`}
                    >
                      <img src={getPhotoUrl(photo)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions for other profiles */}
            {!isOwnProfile && currentUser && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(`/chat?to=${profile.id}`)}
                    data-testid="profile-message-btn"
                    className="flex-1 btn-gold py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    <ChatCircle size={20} weight="fill" /> Message
                  </button>
                  <button
                    data-testid="profile-like-btn"
                    className="px-5 py-3 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all"
                  >
                    <Heart size={24} weight="fill" className="animate-heartbeat" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button data-testid="profile-report-btn" className="flex-1 py-2.5 rounded-xl bg-white/5 text-[#9EABCE] text-sm hover:text-red-400 hover:bg-red-400/5 transition-all flex items-center justify-center gap-2">
                        <Flag size={16} weight="light" /> Report
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0B112B] border-white/10">
                      <DialogHeader>
                        <DialogTitle className="text-white font-['Outfit']">Report User</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-[#9EABCE] text-sm">Reason</Label>
                          <textarea
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            placeholder="Please describe the issue..."
                            rows={3}
                            className="w-full mt-2 bg-white/5 border border-white/10 text-white placeholder:text-[#9EABCE]/40 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                          />
                        </div>
                        <button onClick={handleReport} className="w-full btn-gold py-2.5 rounded-xl text-sm font-bold">
                          Submit Report
                        </button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <button onClick={handleBlock} data-testid="profile-block-btn" className="flex-1 py-2.5 rounded-xl bg-white/5 text-[#9EABCE] text-sm hover:text-red-400 hover:bg-red-400/5 transition-all flex items-center justify-center gap-2">
                    <Prohibit size={16} weight="light" /> Block
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
